import React from "react";
import { getPayPeriodEndDate } from "../../utils/timecardUtils";

const DEFAULT_DEPT = "80001";
const DEFAULT_ACCT = "670500";
const DEFAULT_BU = "32001";
const DEFAULT_SUPERVISOR_NAME = "Patrick Gauthier";

function startOfWeekMonday(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function buildFullAddress(user = {}) {
  const app = user.applicationData || {};

  const street =
    user.street ||
    user.streetAddress ||
    user.address1 ||
    user.address ||
    app.street ||
    app.streetAddress ||
    app.address ||
    "";

  const city = user.city || app.city || "";
  const state = user.state || app.state || "";
  const zip =
    user.zip ||
    user.zipCode ||
    user.zipcode ||
    app.zip ||
    app.zipCode ||
    app.zipcode ||
    "";

  const cityStateZip = [[city, state].filter(Boolean).join(", "), zip]
    .filter(Boolean)
    .join(" ");

  return [street, cityStateZip].filter(Boolean).join(" ").trim();
}

function getPayPeriodStartDate(weekStart) {
  return startOfWeekMonday(new Date(weekStart));
}

export default function PrintableTimecard({
  user,
  rows = [],
  totals = { totalHours: 0, totalPay: 0 },
  weekStart,
  supervisorSignature,
  department = DEFAULT_DEPT,
  accountNumber = DEFAULT_ACCT,
  businessUnit = DEFAULT_BU,
  supervisorName = DEFAULT_SUPERVISOR_NAME,
}) {
  const payStart = getPayPeriodStartDate(weekStart);
  const payEnd = getPayPeriodEndDate(weekStart);
  const fullAddress = buildFullAddress(user);
  const isMobile =
  typeof window !== "undefined" && window.innerWidth <= 768;

  const payRateDisplay =
    typeof user?.payRate === "number" ? `$${user.payRate.toFixed(2)}/hr` : "—";

  const employeeNumber =
    user?.employeeNumber ||
    user?.empNumber ||
    user?.employeeId ||
    "";

  const showEmployeeNumber = !!String(employeeNumber).trim();

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoRow}>
            <img
  src="/assets/thone-sfh-logo.png"
  alt="Trinity Health Of New England Saint Francis Hospital"
  style={styles.logo}
/>
          </div>
          <div style={styles.teamName}>Travelers Championship Medical Team</div>
        </div>

        <div style={styles.headerRight}>
          <div style={styles.sheetTitle}>Employee Time Sheet</div>
        </div>
      </div>

      <div style={styles.infoGrid}>
        <div style={styles.infoCell}>
          <b>Employee Name:</b>&nbsp;{user.displayName || "—"}
        </div>

        <div style={styles.infoCell}>
          {showEmployeeNumber ? (
            <>
              <b>Emp #:</b>&nbsp;{employeeNumber}
            </>
          ) : (
            <>
              <b>Pay Rate:</b>&nbsp;{payRateDisplay}
            </>
          )}
        </div>

        <div style={styles.infoCell}>
          <b>Pay Period:</b>&nbsp;{payStart.toLocaleDateString()} -{" "}
          {payEnd.toLocaleDateString()}
        </div>

        <div style={{ ...styles.infoCell, display: "block" }}>
          <b>Address:</b>
          <br />
          {fullAddress || "—"}
        </div>

        <div style={styles.infoCell}>
          <b>Manager:</b>&nbsp;{supervisorName}
        </div>

        <div style={{ ...styles.infoCell, display: "block" }}>
          <div>
            <b>Dept:</b>&nbsp;{department}
          </div>
          <div>
            <b>Acct:</b>&nbsp;{accountNumber}
          </div>
          <div>
            <b>BU:</b>&nbsp;{businessUnit}
          </div>
        </div>
      </div>

    {isMobile ? (
  <div style={styles.mobileCards}>
    {rows.length === 0 ? (
      <div style={styles.mobileCard}>
        <div style={styles.mobileCardTitle}>No time entries for this week</div>
      </div>
    ) : (
      rows.map((r, i) => (
        <div key={i} style={styles.mobileCard}>
          <div style={styles.mobileCardTitle}>
            {r.day} - {r.date.toLocaleDateString()}
          </div>

          <div style={styles.mobileRow}>
            <span>Time In</span>
            <b>{r.timeIn}</b>
          </div>

          <div style={styles.mobileRow}>
            <span>Time Out</span>
            <b>{r.timeOut}</b>
          </div>

          <div style={styles.mobileRow}>
            <span>Total Hours</span>
            <b>{Number(r.hours || 0).toFixed(2)}</b>
          </div>

          {!showEmployeeNumber && (
            <div style={styles.mobileRow}>
              <span>Total Pay</span>
              <b>{typeof r.pay === "number" ? `$${r.pay.toFixed(2)}` : "—"}</b>
            </div>
          )}

          <div style={styles.mobileRow}>
            <span>Provider Level</span>
            <b>{r.providerLevel || "—"}</b>
          </div>
        </div>
      ))
    )}
  </div>
) : (
  <div style={{ width: "100%", overflowX: "auto" }}>
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Date</th>
          <th style={styles.th}>Day</th>
          <th style={styles.th}>Time In</th>
          <th style={styles.th}>Time Out</th>
          <th style={styles.th}>Total Hours</th>
          {!showEmployeeNumber && <th style={styles.th}>Total Pay</th>}
          <th style={styles.th}>Provider Level</th>
        </tr>
      </thead>

      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td style={styles.td}>{r.date.toLocaleDateString()}</td>
            <td style={styles.td}>{r.day}</td>
            <td style={styles.td}>{r.timeIn}</td>
            <td style={styles.td}>{r.timeOut}</td>
            <td style={styles.td}>{Number(r.hours || 0).toFixed(2)}</td>
            {!showEmployeeNumber && (
              <td style={styles.td}>
                {typeof r.pay === "number" ? `$${r.pay.toFixed(2)}` : "—"}
              </td>
            )}
            <td style={styles.td}>{r.providerLevel || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}
    

      <div style={styles.totals}>
        <div>
          <b>Total Hours:</b> {Number(totals.totalHours || 0).toFixed(2)}
        </div>
        {!showEmployeeNumber && (
          <div>
            <b>Total Pay:</b> ${Number(totals.totalPay || 0).toFixed(2)}
          </div>
        )}
      </div>

      <div style={styles.signatures}>
        <div style={styles.signatureBlock}>
          <div>Employee Signature:</div>
          <div style={styles.signatureLine}>
            {user.signatureURL && (
              <img
                src={user.signatureURL}
                alt="Employee signature"
                style={styles.sigImg}
              />
            )}
          </div>
          <div>Date: {payEnd.toLocaleDateString()}</div>
        </div>

        <div style={styles.signatureBlock}>
          <div>Supervisor Signature:</div>
          <div style={styles.signatureLine}>
            {supervisorSignature && (
              <img
                src={supervisorSignature}
                alt="Supervisor signature"
                style={styles.sigImg}
              />
            )}
          </div>
          <div>Date: {payEnd.toLocaleDateString()}</div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: 20,
    pageBreakAfter: "always",
    background: "white",
    color: "#000",
  },
  header: {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  marginBottom: 18,
},

  headerLeft: {
    flex: 1,
  },
  headerRight: {
    minWidth: 250,
    textAlign: "right",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
  },
  logo: {
    maxWidth: 220,
    height: "auto",
    objectFit: "contain",
  },
  teamName: {
    marginTop: 4,
    fontWeight: 700,
    fontSize: 14,
    color: "#111827",
  },
  sheetTitle: {
    fontWeight: 900,
    fontSize: 18,
    marginBottom: 8,
  },
  infoGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  border: "1px solid #000",
  marginBottom: 20,
},
  infoCell: {
    padding: "8px 10px",
    borderRight: "1px solid #000",
    borderBottom: "1px solid #000",
    minHeight: 44,
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    lineHeight: 1.25,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
  textAlign: "left",
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  background: "#e2e8f0",
  fontWeight: 700,
  whiteSpace: "nowrap",
},

td: {
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  whiteSpace: "nowrap",
},

  totals: {
    marginTop: 10,
    display: "flex",
    justifyContent: "flex-end",
    gap: 30,
    fontSize: 16,
    fontWeight: 600,
  },
  
  signatures: {
  marginTop: 36,
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  gap: 24,
},

  signatureBlock: {
    flex: 1,
    maxWidth: "48%",
  },
  signatureLine: {
    height: 60,
    display: "flex",
    alignItems: "flex-end",
    borderBottom: "1px solid #000",
    marginTop: 6,
    marginBottom: 6,
  },
  sigImg: {
    height: 50,
    objectFit: "contain",
  },
  mobileCards: {
  display: "grid",
  gap: 12,
  marginTop: 12,
},

mobileCard: {
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: 14,
  background: "#f8fafc",
},

mobileCardTitle: {
  fontWeight: 900,
  fontSize: 15,
  marginBottom: 10,
  color: "#0f172a",
},

mobileRow: {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  padding: "7px 0",
  borderTop: "1px solid #e2e8f0",
  fontSize: 14,
},

};